import clsx from 'clsx';
import {
  resolveStatusTone,
  type StatusTone
} from '@utils/constants/status-map';

// Text tones bumped from -700 to -800 for WCAG AA 4.5:1 on the low-opacity
// tinted backgrounds these badges use (axe measured -700 at 4.17-4.32:1).
// The one exception is ``zinc``: it is the neutral "count" chip and sits
// next to muted tab labels + subtitles, so a -800 read as near-black and
// looked heavier than the surrounding chrome. Its background tint is
// bumped to ``/20`` so the text tone can drop to -700 while keeping AA.
const colors = {
  red: 'bg-red-500/15 text-red-800 group-data-hover:bg-red-500/25 dark:bg-red-500/10 dark:text-red-400 dark:group-data-hover:bg-red-500/20',
  orange:
    'bg-orange-500/15 text-orange-800 group-data-hover:bg-orange-500/25 dark:bg-orange-500/10 dark:text-orange-400 dark:group-data-hover:bg-orange-500/20',
  amber:
    'bg-amber-400/20 text-amber-800 group-data-hover:bg-amber-400/30 dark:bg-amber-400/10 dark:text-amber-400 dark:group-data-hover:bg-amber-400/15',
  yellow:
    'bg-yellow-400/20 text-yellow-800 group-data-hover:bg-yellow-400/30 dark:bg-yellow-400/10 dark:text-yellow-300 dark:group-data-hover:bg-yellow-400/15',
  lime: 'bg-lime-400/20 text-lime-800 group-data-hover:bg-lime-400/30 dark:bg-lime-400/10 dark:text-lime-300 dark:group-data-hover:bg-lime-400/15',
  green:
    'bg-green-500/15 text-green-800 group-data-hover:bg-green-500/25 dark:bg-green-500/10 dark:text-green-400 dark:group-data-hover:bg-green-500/20',
  emerald:
    'bg-emerald-500/15 text-emerald-800 group-data-hover:bg-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-400 dark:group-data-hover:bg-emerald-500/20',
  teal: 'bg-teal-500/15 text-teal-800 group-data-hover:bg-teal-500/25 dark:bg-teal-500/10 dark:text-teal-300 dark:group-data-hover:bg-teal-500/20',
  cyan: 'bg-cyan-400/20 text-cyan-800 group-data-hover:bg-cyan-400/30 dark:bg-cyan-400/10 dark:text-cyan-300 dark:group-data-hover:bg-cyan-400/15',
  sky: 'bg-sky-500/15 text-sky-800 group-data-hover:bg-sky-500/25 dark:bg-sky-500/10 dark:text-sky-300 dark:group-data-hover:bg-sky-500/20',
  blue: 'bg-blue-500/15 text-blue-800 group-data-hover:bg-blue-500/25 dark:text-blue-400 dark:group-data-hover:bg-blue-500/25',
  indigo:
    'bg-indigo-500/15 text-indigo-800 group-data-hover:bg-indigo-500/25 dark:text-indigo-400 dark:group-data-hover:bg-indigo-500/20',
  violet:
    'bg-violet-500/15 text-violet-800 group-data-hover:bg-violet-500/25 dark:text-violet-400 dark:group-data-hover:bg-violet-500/20',
  purple:
    'bg-purple-500/15 text-purple-800 group-data-hover:bg-purple-500/25 dark:text-purple-400 dark:group-data-hover:bg-purple-500/20',
  fuchsia:
    'bg-fuchsia-400/15 text-fuchsia-800 group-data-hover:bg-fuchsia-400/25 dark:bg-fuchsia-400/10 dark:text-fuchsia-400 dark:group-data-hover:bg-fuchsia-400/20',
  pink: 'bg-pink-400/15 text-pink-800 group-data-hover:bg-pink-400/25 dark:bg-pink-400/10 dark:text-pink-400 dark:group-data-hover:bg-pink-400/20',
  rose: 'bg-rose-400/15 text-rose-800 group-data-hover:bg-rose-400/25 dark:bg-rose-400/10 dark:text-rose-400 dark:group-data-hover:bg-rose-400/20',
  zinc: 'bg-zinc-600/20 text-zinc-700 group-data-hover:bg-zinc-600/30 dark:bg-white/10 dark:text-zinc-300 dark:group-data-hover:bg-white/15',
  // Inherits the parent's ``currentColor`` for BOTH text and tint. Used
  // inside active nav / tab labels so the count chip switches white on
  // an active tab and muted on an inactive one, tracking the label —
  // fixed the "dark chip on a blue active tab" mismatch (#2226 tab
  // strip). ``text-current`` == inherit; ``bg-current`` with an alpha
  // gives the same colour a low-opacity fill.
  current:
    'bg-current/15 text-current group-data-hover:bg-current/25 dark:bg-current/15 dark:group-data-hover:bg-current/25'
};

const TONE_CLASSES: Record<StatusTone, string> = {
  success:
    'bg-green-500/15 text-green-800 dark:bg-green-500/10 dark:text-green-400',
  warn: 'bg-amber-400/20 text-amber-800 dark:bg-amber-400/10 dark:text-amber-400',
  danger: 'bg-red-500/15 text-red-800 dark:bg-red-500/10 dark:text-red-400',
  info: 'bg-blue-500/15 text-blue-800 dark:bg-blue-500/10 dark:text-blue-400',
  neutral: 'bg-zinc-600/20 text-zinc-700 dark:bg-white/10 dark:text-zinc-300',
  purple:
    'bg-purple-500/15 text-purple-800 dark:bg-purple-400/10 dark:text-purple-400',
  orange:
    'bg-orange-500/15 text-orange-800 dark:bg-orange-500/10 dark:text-orange-400'
};

type BadgeProps = { color?: keyof typeof colors };

/** Low-level badge with a Tailwind color slot — use StatusBadge for status strings. */
export function Badge({
  color = 'zinc',
  className,
  ...props
}: BadgeProps & React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'inline-flex items-center gap-x-1.5 rounded-2xl px-1.5 py-0.5 text-sm/5 font-medium sm:text-xs/5 forced-colors:outline',
        colors[color]
      )}
    />
  );
}

interface StatusBadgeProps {
  /** Raw status string from the backend — resolved through the shared status map. */
  status: string;
  /** Override the display label (defaults to status). */
  label?: string;
  /** Override the tone directly instead of resolving from status string. */
  tone?: StatusTone;
  className?: string;
}

/** Renders a pill badge using the shared status→color map. Drop-in for all status chips. */
export function StatusBadge({
  status,
  label,
  tone,
  className
}: StatusBadgeProps) {
  const resolvedTone = tone ?? resolveStatusTone(status);
  return (
    <span
      className={clsx(
        'inline-flex w-fit items-center px-2 py-0.5 text-xs font-medium rounded-full truncate',
        TONE_CLASSES[resolvedTone],
        className
      )}
    >
      {label ?? status}
    </span>
  );
}
